import { ERROR_MESSAGES } from '@tebuto/functions/api'
import RequestGate from '@tebuto/layout/RequestGate'
import { getStripeCheckoutReturnSessionId } from '@tebuto/payment/stripe/stripe-checkout-return'
import React, { createContext, Dispatch, SetStateAction, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ObjectShape } from 'yup'
import { BookEventFormValues } from './BookingEvent'
import { buildCheckoutRestoreUpdates } from './checkout-restore.util'
import {
    dedupeIdenticalBookableSlots,
    inferRuleCategoryIdsFromParsedBookableEvents,
    normalizeFlexibleBookingEvents
} from './flexible-booking-events'
import LimitExceeded from './LimitExceeded'
import { loadPendingCheckout } from './payment/pending-checkout-storage'
import {
    Booking,
    CreditOverviewItem,
    EventCategory,
    EventLocation,
    EventLocationSelection,
    EventTherapist,
    ParsedBookableEvent,
    PaymentConfiguration,
    Therapist
} from './types'

export type BookingCalendarState =
    | 'category-selection'
    | 'calendar'
    | 'booking'
    | 'login'
    | 'payment'
    | 'success'
    | 'failure'
export type BookingNavigationDirection = 'forward' | 'back' | null

export type BookingHooks = {
    useBookableEvents: () => {
        events: ParsedBookableEvent[] | undefined
        categories: { [categoryId: number]: EventCategory }
        therapists?: { [therapistId: number]: EventTherapist }
        showLoading: boolean
        error: any
        mutate: (() => void) | (() => Promise<void>)
    }
    useCheckBookingAvailability: () => {
        checkBookingAvailability: (options: {
            event: ParsedBookableEvent
            onSuccess: (response: { isAvailable: boolean }) => void
        }) => void
        isLoading: boolean
        error: string | null
    }
    useUnclaimBooking: () => {
        unclaimBooking: (event: ParsedBookableEvent) => void
        isLoading: boolean
        error: string | null
    }
    usePaymentConfiguration: () => {
        paymentConfiguration: PaymentConfiguration | undefined
        showLoading: boolean
        error: any
        mutate: (() => void) | (() => Promise<void>)
    }
    useGeneratePayPalClientToken: () => {
        data: { token: string } | undefined
        showLoading: boolean
        error: any
        mutate: (() => void) | (() => Promise<void>)
    }
    useCreateCheckoutOrder: () => {
        createCheckoutOrder: (options: {
            event: ParsedBookableEvent
            firstName: string
            lastName: string
            email: string
            address?: {
                streetAndNumber: string
                city: { zip: string; name: string }
            }
        }) => Promise<{ id: string; url: string }>
        isLoading: boolean
        error: string | null
    }
    useCreateCheckoutSession: () => {
        createCheckoutSession: (options: {
            event: ParsedBookableEvent
            firstName: string
            lastName: string
            email: string
            address?: {
                streetAndNumber: string
                city: { zip: string; name: string }
            }
            onlinePaymentMethod?: PaymentConfiguration['onlinePaymentMethods'][number]
        }) => Promise<{ id: string; clientSecret: string; returnUrl?: string }>
        isLoading: boolean
        error: string | null
    }
    useBookEvent: () => {
        bookEvent: (
            options: { event: ParsedBookableEvent; selectedLocation: EventLocationSelection } & Record<string, any>
        ) => Promise<Booking>
        isLoading: boolean
        error: string | null
    }
    /** Optional hook to check available credits for the logged-in client. Only used in client app, not public widget. */
    useCreditCheck?: () => {
        credits: CreditOverviewItem[] | undefined
        showLoading: boolean
        error: unknown
    }
}

export type BookingContextData = BookingHooks & {
    therapist: Therapist
    events: ParsedBookableEvent[]
    refreshEvents: (() => void) | (() => Promise<void>)
    categories: Record<number, EventCategory>
    therapists: Record<number, EventTherapist>
    bookingEvent: ParsedBookableEvent | undefined
    setBookingEvent: Dispatch<SetStateAction<ParsedBookableEvent | undefined>>

    // navigation
    state: BookingCalendarState
    setState: Dispatch<SetStateAction<BookingCalendarState>>
    navigationDirection: BookingNavigationDirection
    setNavigationDirection: Dispatch<SetStateAction<BookingNavigationDirection>>

    // booking
    bookingData: BookEventFormValues | null
    setBookingData: Dispatch<SetStateAction<BookEventFormValues | null>>
    overviewSelectedLocation: EventLocation | null
    setOverviewSelectedLocation: Dispatch<SetStateAction<EventLocation | null>>
    bookingResult: Booking | null
    setBookingResult: Dispatch<SetStateAction<Booking | null>>
    isEventAlreadyBookedDialogOpen: boolean
    setIsEventAlreadyBookedDialogOpen: Dispatch<SetStateAction<boolean>>
    layout?: 'small' | 'large'
    requireCaptcha?: boolean
    requireTermsConsent?: boolean
    showQuickFilters?: boolean
    showLocationQuickFilter?: boolean
    additionalBookingFieldsBefore?: React.ReactNode
    additionalInitialValues?: Record<string, any>
    additionalBookingValidationSchemaFields?: ObjectShape
    // Fixed category ID for move booking flow - locks calendar to only show events of this category
    fixedCategoryId?: number
    skipInitialCategorySelection?: boolean
    previewMode?: boolean
    showTherapistProfile?: boolean
    profileUrl?: string
    categorySelectionHeading?: string

    // Category selection state
    selectedCategoryId: number | null
    setSelectedCategoryId: Dispatch<SetStateAction<number | null>>

    // Credit state — only populated when useCreditCheck hook is provided
    credits: CreditOverviewItem[]
    useCreditsForBooking: boolean
    setUseCreditsForBooking: Dispatch<SetStateAction<boolean>>

    bookingNote?: string | null

    checkoutRestoreScopeKey?: string

    // widget login integration (optional — only set by public booking widget)
    isClientAuthenticated?: boolean
    onClickAlreadyHaveAccount?: () => void
}

export const BookingContext = createContext<BookingContextData>({} as BookingContextData)

type BookingContextProviderProps = BookingHooks & {
    therapist: Therapist
    children: React.ReactNode | React.ReactNode[]
    layout?: 'small' | 'large'
    requireCaptcha?: boolean
    requireTermsConsent?: boolean
    showQuickFilters?: boolean
    showLocationQuickFilter?: boolean
    additionalBookingFieldsBefore?: React.ReactNode
    additionalInitialValues?: Record<string, any>
    additionalBookingValidationSchemaFields?: ObjectShape
    // Fixed category ID for move booking flow - locks calendar to only show events of this category
    fixedCategoryId?: number
    skipInitialCategorySelection?: boolean
    previewMode?: boolean
    showTherapistProfile?: boolean
    profileUrl?: string
    categorySelectionHeading?: string
    bookingNote?: string | null
    checkoutRestoreScopeKey?: string
    isClientAuthenticated?: boolean
    onClickAlreadyHaveAccount?: () => void
}

export function BookingContextProvider({
    children,
    layout = 'small',
    requireCaptcha = false,
    ...props
}: BookingContextProviderProps) {
    const [navigationDirection, setNavigationDirection] = useState<BookingNavigationDirection>(null)
    const [bookingEvent, setBookingEvent] = useState<ParsedBookableEvent>()
    const [bookingData, setBookingData] = useState<BookEventFormValues | null>(null)
    const [overviewSelectedLocation, setOverviewSelectedLocation] = useState<EventLocation | null>(null)
    const [bookingResult, setBookingResult] = useState<Booking | null>(null)
    const [isEventAlreadyBookedDialogOpen, setIsEventAlreadyBookedDialogOpen] = useState(false)
    const [useCreditsForBooking, setUseCreditsForBooking] = useState(true)
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null)
    const [state, setState] = useState<BookingCalendarState>('calendar')
    const initialStateResolved = useRef(false)
    const checkoutRestoreApplied = useRef(false)

    const { events, categories, therapists = {}, showLoading, error, mutate } = props.useBookableEvents()
    const creditData = props.useCreditCheck?.()
    const credits = creditData?.credits ?? []

    useLayoutEffect(() => {
        if (checkoutRestoreApplied.current || typeof window === 'undefined' || !props.checkoutRestoreScopeKey) {
            return
        }

        const sessionId = getStripeCheckoutReturnSessionId(new URLSearchParams(window.location.search))
        const pending = loadPendingCheckout(props.checkoutRestoreScopeKey)

        if (!sessionId && !pending) {
            return
        }

        checkoutRestoreApplied.current = true
        initialStateResolved.current = true

        if (pending) {
            const restore = buildCheckoutRestoreUpdates(pending)
            setBookingEvent(restore.bookingEvent)
            setBookingData(restore.bookingData)
            setOverviewSelectedLocation(restore.overviewSelectedLocation)
        }

        setState('payment')
    }, [props.checkoutRestoreScopeKey])

    useEffect(() => {
        if (initialStateResolved.current) return
        if (!categories || Object.keys(categories).length === 0) return
        initialStateResolved.current = true
        if (Object.keys(categories).length > 1 && !props.fixedCategoryId && !props.skipInitialCategorySelection) {
            setState('category-selection')
        }
    }, [categories, props.fixedCategoryId, props.skipInitialCategorySelection])

    if (error === ERROR_MESSAGES.PlanLimitExceeded) {
        return <LimitExceeded therapist={props.therapist} />
    }

    return (
        <RequestGate
            data={events && categories ? { events, categories, therapists } : undefined}
            showLoading={showLoading}
            error={error}
            retry={mutate}
        >
            {({ events, categories, therapists }) => {
                // The calendar locks/filters by `fixedCategoryId ?? selectedCategoryId`. Normalize flexible
                // slots against the same lock so a locked category collapses sibling-category variants of a
                // rule into that category instead of leaking them through with their own (wrong) category.
                const effectiveCategoryLock = props.fixedCategoryId ?? selectedCategoryId
                const normalizedEvents = dedupeIdenticalBookableSlots(
                    normalizeFlexibleBookingEvents(
                        inferRuleCategoryIdsFromParsedBookableEvents(events),
                        categories as Record<number, EventCategory>,
                        effectiveCategoryLock
                    )
                )

                return (
                    <BookingContext.Provider
                        value={{
                            state,
                            setState,
                            events: normalizedEvents,
                            refreshEvents: mutate,
                            categories,
                            therapists,
                            bookingEvent,
                            setBookingEvent,
                            navigationDirection,
                            setNavigationDirection,
                            bookingData,
                            setBookingData,
                            overviewSelectedLocation,
                            setOverviewSelectedLocation,
                            bookingResult,
                            setBookingResult,
                            isEventAlreadyBookedDialogOpen,
                            setIsEventAlreadyBookedDialogOpen,
                            requireCaptcha,
                            layout,
                            credits,
                            useCreditsForBooking,
                            setUseCreditsForBooking,
                            selectedCategoryId,
                            setSelectedCategoryId,
                            ...props
                        }}
                    >
                        {children}
                    </BookingContext.Provider>
                )
            }}
        </RequestGate>
    )
}
